import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { invoicesAPI, customersAPI } from '../../services/api';
import { useCompany } from '../../services/companyState';
import {
  UNITS,
  unitLabel,
  formatEuro,
} from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import '../Offers/OfferForm.css';

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  if (isNaN(d)) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = () => ({
  customer_id: '',
  invoice_date: todayISO(),
  service_date: todayISO(),
  due_date: addDays(todayISO(), 14),
  title: '',
  notes: '',
});

const parseNumber = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const lineNet = (item) => parseNumber(item.quantity) * parseNumber(item.unit_price);
const lineVat = (item) => lineNet(item) * (parseNumber(item.vat_rate) / 100);
const lineGross = (item) => lineNet(item) + lineVat(item);
const lineTotal = lineNet;

const VAT_RATES = ['0', '7', '19'];

const makeItem = (position, defaultVatRate) => ({
  description: '',
  quantity: '1',
  unit_price: '0',
  unit: 'pauschal',
  vat_rate: String(defaultVatRate ?? 0),
  position,
});

const InvoiceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const defaultVatRate = isKleinunternehmer ? 0 : Number(company?.vat_default_rate ?? 19);

  const [form, setForm] = useState(EMPTY_FORM());
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const customerResp = await customersAPI.getAll({ active_only: false, limit: 500 });
        if (cancelled) return;
        setCustomers(customerResp.data || []);

        if (isEdit) {
          const { data } = await invoicesAPI.getById(id);
          if (cancelled) return;
          if (data.status !== 'draft') {
            setReadOnly(true);
            toast('Diese Rechnung ist nicht mehr im Entwurf-Status – nur Ansicht.', {
              icon: 'ℹ️',
            });
          }
          setForm({
            customer_id: String(data.customer_id || ''),
            invoice_date: data.invoice_date || todayISO(),
            service_date: data.service_date || todayISO(),
            due_date: data.due_date || addDays(todayISO(), 14),
            title: data.title || '',
            notes: data.notes || '',
          });
          setItems(
            (data.items || [])
              .slice()
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((it, idx) => ({
                description: it.description || '',
                quantity: String(it.quantity ?? '1'),
                unit_price: String(it.unit_price ?? '0'),
                unit: it.unit || 'pauschal',
                vat_rate: String(it.vat_rate ?? 0),
                position: idx,
              }))
          );
        }
      } catch (err) {
        toast.error(
          err.response?.status === 404
            ? 'Rechnung nicht gefunden'
            : 'Fehler beim Laden'
        );
        if (isEdit) navigate('/invoices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const totals = useMemo(() => {
    let net = 0;
    let vat = 0;
    const byRate = {};
    for (const it of items) {
      const n = lineNet(it);
      const r = parseNumber(it.vat_rate);
      const v = n * (r / 100);
      net += n;
      vat += v;
      const key = r.toFixed(2);
      if (!byRate[key]) byRate[key] = { rate: r, net: 0, vat: 0 };
      byRate[key].net += n;
      byRate[key].vat += v;
    }
    return {
      net, vat, gross: net + vat,
      breakdown: Object.values(byRate).sort((a, b) => a.rate - b.rate),
    };
  }, [items]);
  const total = useMemo(
    () => items.reduce((sum, it) => sum + lineTotal(it), 0),
    [items]
  );

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'invoice_date' && !isEdit) {
        next.due_date = addDays(value, 14);
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const addItem = () => {
    setItems((prev) => [...prev, makeItem(prev.length, defaultVatRate)]);
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const next = {};
    if (!form.customer_id) next.customer_id = 'Bitte Kunde auswählen';
    if (!form.invoice_date) next.invoice_date = 'Rechnungsdatum erforderlich';
    if (!form.service_date) next.service_date = 'Leistungsdatum erforderlich';
    if (!form.due_date) next.due_date = 'Fälligkeitsdatum erforderlich';
    if (items.length === 0) next.items = 'Mindestens eine Position hinzufügen';
    items.forEach((it, idx) => {
      if (!it.description.trim()) next[`item_${idx}_description`] = 'Beschreibung fehlt';
      if (parseNumber(it.quantity) <= 0) next[`item_${idx}_quantity`] = 'Menge > 0';
      if (parseNumber(it.unit_price) < 0) next[`item_${idx}_unit_price`] = 'Preis ≥ 0';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!validate()) {
      toast.error('Bitte Eingaben prüfen');
      return;
    }
    setSaving(true);
    const payload = {
      customer_id: Number(form.customer_id),
      invoice_date: form.invoice_date,
      service_date: form.service_date,
      due_date: form.due_date,
      title: form.title || null,
      service_description: form.title || 'Leistungen laut Positionen',
      notes: form.notes || null,
      items: items.map((it, idx) => ({
        description: it.description,
        quantity: parseNumber(it.quantity),
        unit_price: parseNumber(it.unit_price),
        unit: it.unit,
        vat_rate: isKleinunternehmer ? 0 : parseNumber(it.vat_rate),
        position: idx,
      })),
    };
    try {
      if (isEdit) {
        await invoicesAPI.update(id, payload);
        toast.success('Rechnung aktualisiert');
      } else {
        await invoicesAPI.create(payload);
        toast.success('Rechnung erstellt');
      }
      navigate('/invoices');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const map = {};
        detail.forEach((d) => {
          const field = d.loc?.slice(-1)?.[0];
          if (field) map[field] = d.msg;
        });
        setErrors(map);
      }
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const disabled = saving || readOnly;

  return (
    <div>
      <div className="content-header">
        <div>
          <Link to="/invoices" className="back-link">
            <ArrowLeft size={14} />
            Zurück zur Übersicht
          </Link>
          <h1>{isEdit ? 'Rechnung bearbeiten' : 'Neue Rechnung'}</h1>
          <p>Positionen hinzufügen und Rechnungsdetails festlegen</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="content-body offer-form-grid">
        <div className="card">
          <div className="card-header"><h2>Rechnungsdaten</h2></div>
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="customer_id">Kunde *</label>
                <select
                  id="customer_id"
                  name="customer_id"
                  value={form.customer_id}
                  onChange={handleField}
                  className={`form-input ${errors.customer_id ? 'is-invalid' : ''}`}
                  disabled={disabled}
                >
                  <option value="">-- Kunde wählen --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.city ? `(${c.city})` : ''}
                    </option>
                  ))}
                </select>
                {errors.customer_id && (
                  <div className="field-error">{errors.customer_id}</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="title">Titel / Betreff</label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={form.title}
                  onChange={handleField}
                  placeholder="z.B. Webseitenrelaunch April 2026"
                  className="form-input"
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="invoice_date">Rechnungsdatum *</label>
                <input
                  id="invoice_date"
                  name="invoice_date"
                  type="date"
                  value={form.invoice_date}
                  onChange={handleField}
                  className={`form-input ${errors.invoice_date ? 'is-invalid' : ''}`}
                  disabled={disabled}
                />
                {errors.invoice_date && <div className="field-error">{errors.invoice_date}</div>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="service_date">Leistungsdatum *</label>
                <input
                  id="service_date"
                  name="service_date"
                  type="date"
                  value={form.service_date}
                  onChange={handleField}
                  className={`form-input ${errors.service_date ? 'is-invalid' : ''}`}
                  disabled={disabled}
                />
                {errors.service_date && <div className="field-error">{errors.service_date}</div>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="due_date">Fälligkeit *</label>
                <input
                  id="due_date"
                  name="due_date"
                  type="date"
                  value={form.due_date}
                  onChange={handleField}
                  className={`form-input ${errors.due_date ? 'is-invalid' : ''}`}
                  disabled={disabled}
                />
                {errors.due_date && <div className="field-error">{errors.due_date}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Positionen</h2>
            <button
              type="button"
              onClick={addItem}
              className="btn btn-outline btn-sm"
              disabled={disabled}
            >
              <Plus size={14} />
              Position hinzufügen
            </button>
          </div>
          <div className="form-section">
            {items.length === 0 ? (
              <div className="empty-items">
                Noch keine Positionen. Klicken Sie oben auf "Position hinzufügen".
              </div>
            ) : (
              <div className="line-items">
                {items.map((it, idx) => (
                    <div key={idx} className="line-item">
                      <div className="line-item-grid">
                        <div className="li-field li-description">
                          <label className="form-label">Beschreibung</label>
                          <input
                            type="text"
                            value={it.description}
                            onChange={(e) => updateItem(idx, 'description', e.target.value)}
                            className={`form-input ${errors[`item_${idx}_description`] ? 'is-invalid' : ''}`}
                            placeholder="Position beschreiben..."
                            disabled={disabled}
                          />
                        </div>
                        <div className="li-field li-qty">
                          <label className="form-label">Menge</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={it.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                            className={`form-input ${errors[`item_${idx}_quantity`] ? 'is-invalid' : ''}`}
                            disabled={disabled}
                          />
                        </div>
                        <div className="li-field li-price">
                          <label className="form-label">Einzelpreis (€)</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={it.unit_price}
                            onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                            className={`form-input ${errors[`item_${idx}_unit_price`] ? 'is-invalid' : ''}`}
                            disabled={disabled}
                          />
                        </div>
                        <div className="li-field li-unit">
                          <label className="form-label">Einheit</label>
                          <select
                            value={it.unit}
                            onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                            className="form-input"
                            disabled={disabled}
                          >
                            {UNITS.map((u) => (
                              <option key={u.value} value={u.value}>{u.label}</option>
                            ))}
                          </select>
                        </div>
                        {!isKleinunternehmer && (
                          <div className="li-field li-vat" style={{ minWidth: 90 }}>
                            <label className="form-label">USt %</label>
                            <select
                              value={it.vat_rate}
                              onChange={(e) => updateItem(idx, 'vat_rate', e.target.value)}
                              className="form-input"
                              disabled={disabled}
                            >
                              {VAT_RATES.map((r) => (
                                <option key={r} value={r}>{r} %</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="li-field li-total">
                          <label className="form-label">Gesamt</label>
                          <div className="li-total-value">
                            {formatEuro(lineGross(it))}
                            {!isKleinunternehmer && parseNumber(it.vat_rate) > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 400 }}>
                                (netto {formatEuro(lineNet(it))})
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="li-field li-actions">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeItem(idx)}
                            disabled={disabled}
                            title="Position entfernen"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            )}
            {errors.items && <div className="field-error">{errors.items}</div>}

            <div className="offer-total">
              {isKleinunternehmer ? (
                <>
                  <div className="offer-total-row">
                    <span>Rechnungsbetrag</span>
                    <span className="offer-total-value">{formatEuro(totals.net)}</span>
                  </div>
                  <div className="kleinunternehmer-notice">
                    Gemäß §19 UStG (Kleinunternehmer) wird keine Umsatzsteuer berechnet.
                  </div>
                </>
              ) : (
                <>
                  <div className="offer-total-row" style={{ fontSize: '0.95rem' }}>
                    <span>Nettobetrag</span>
                    <span>{formatEuro(totals.net)}</span>
                  </div>
                  {totals.breakdown.filter((b) => b.rate > 0).map((b) => (
                    <div key={b.rate} className="offer-total-row" style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                      <span>USt {b.rate.toFixed(0)}% (auf {formatEuro(b.net)})</span>
                      <span>{formatEuro(b.vat)}</span>
                    </div>
                  ))}
                  {totals.breakdown.some((b) => b.rate === 0) && (
                    <div className="offer-total-row" style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      <span>Steuerfrei (auf {formatEuro(totals.breakdown.find((b) => b.rate === 0).net)})</span>
                      <span>0,00 €</span>
                    </div>
                  )}
                  <div className="offer-total-row" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem', marginTop: '0.4rem' }}>
                    <span>Rechnungsbetrag (brutto)</span>
                    <span className="offer-total-value">{formatEuro(totals.gross)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2>Anmerkungen</h2></div>
          <div className="form-section">
            <div className="form-group">
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleField}
                className="form-input"
                rows={4}
                placeholder="Zusätzliche Anmerkungen auf der Rechnung"
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Link to="/invoices" className="btn btn-outline" aria-disabled={saving}>
            Abbrechen
          </Link>
          {!readOnly && (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={14} />
              {saving
                ? 'Speichern...'
                : isEdit
                ? 'Änderungen speichern'
                : 'Rechnung erstellen'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
